import React, { createContext, useContext, useState, ReactNode } from 'react';

interface QueryBuilderContextType {
  queryBuilder: any;
  setCondition: (field: string, operator: string, value: any) => void;
  removeCondition: (field: string) => void;
  getConditionValue: (field: string) => { operator: string; value: any } | null;
  setMultipleConditions: (conditions: { field: string; operator: string; value: any, shouldRemove?: boolean }[]) => void;
  removeAllConditions: (ignoreFields?: string[], defaultConditions?: Record<string, any>) => void;
  removeMultipleConditions: (fields: string[], defaultConditions?: Record<string, any>) => void;
}

export const QueryBuilderContext = createContext<QueryBuilderContextType | undefined>(undefined);

export const QueryBuilderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [queryBuilder, setQueryBuilder] = useState<any>({
    query: { $and: [] }
  });

  const setCondition = (field: string, operator: string, value: any) => {
    const previousConditions = queryBuilder.query?.$and || [];

    // Remove any existing condition for this field
    const filteredConditions = previousConditions.filter(
      (condition) => !Object.keys(condition).includes(field)
    );

    const updatedConditions = [
      ...filteredConditions,
      { [field]: { [operator]: value } },
    ];

    const updatedQuery = { query: { $and: updatedConditions } };
    setQueryBuilder(updatedQuery);
    return updatedQuery;
  };



  const removeCondition = (field: string) => {
    const updatedConditions = (queryBuilder.query?.$and || []).filter((condition) => {
      // Remove condition if it references the given field
      return !condition[field]; // Keep condition if field is not in it
    });

    setQueryBuilder({ query: { $and: updatedConditions } });
  };


  const getConditionValue = (field: string, operator?: string) => {
    // Assume queryBuilder is available in this scope
    const andConditions = queryBuilder?.query?.$and || [];

    for (const condition of andConditions) {
      if (condition[field]) {
        if (operator) {
          // Return value for specific operator or null if not present
          return condition[field][operator] ?? null;
        } else {
          // Return all operators and values for the field
          return { ...condition[field] };
        }
      }
    }

    return null;
  };


  const setMultipleConditions = (
    conditions: { field: string; operator: string; value: any; shouldRemove?: boolean }[]
  ) => {
    let updatedConditions = [...(queryBuilder.query?.$and || [])];

    const fieldMap: Record<string, Record<string, any>> = {};

    // Group new operators under each field
    for (const { field, operator, value, shouldRemove } of conditions) {
      if (!fieldMap[field]) fieldMap[field] = {};
      fieldMap[field][operator] = value;
      if (shouldRemove) {
        fieldMap[field].shouldRemove = shouldRemove;
      }
    }

    // Remove existing conditions for the fields being updated
    const fieldsToUpdate = new Set(Object.keys(fieldMap));
    updatedConditions = updatedConditions.filter(
      (condition) => !Object.keys(condition).some((field) => fieldsToUpdate.has(field))
    );

    // Create merged conditions
    const newConditions = Object.entries(fieldMap).filter(([field, ops]) => !ops.shouldRemove).map(([field, ops]) => ({
      [field]: ops,
    }));

    const updatedQuery = { query: { $and: [...updatedConditions, ...newConditions] } };
    setQueryBuilder(updatedQuery);
    return updatedQuery;
  };

  const removeAllConditions = (
    ignoreFields: string[] = [],
    defaultConditions: Record<string, any> = {}
  ) => {
    const existingConditions = queryBuilder.query?.$and || [];

    const retainedConditions: Record<string, any>[] = [];

    ignoreFields.forEach((field) => {
      if (defaultConditions[field]) {
        // Use the default condition if provided
        retainedConditions.push({ [field]: defaultConditions[field] });
      } else {
        // Otherwise, retain the existing condition if it exists
        const existing = existingConditions.find((cond) =>
          Object.prototype.hasOwnProperty.call(cond, field)
        );
        if (existing) {
          retainedConditions.push(existing);
        }
      }
    });

    setQueryBuilder({ query: { $and: retainedConditions } });
  };


  const removeMultipleConditions = (
    fieldsToRemove: string[],
    defaultConditions: Record<string, any> = {}
  ) => {
    const existingConditions = queryBuilder.query?.$and || [];

    // Filter out conditions we want to remove
    const filteredConditions = existingConditions.filter(
      (condition) => !Object.keys(condition).some((field) => fieldsToRemove.includes(field))
    );

    // Add default conditions for removed fields (if specified)
    const fallbackConditions = fieldsToRemove
      .filter((field) => defaultConditions[field])
      .map((field) => ({
        [field]: defaultConditions[field],
      }));

    setQueryBuilder({ query: { $and: [...filteredConditions, ...fallbackConditions] } });
  };


  return (
    <QueryBuilderContext.Provider value={{ queryBuilder, setCondition, removeCondition, getConditionValue, setMultipleConditions, removeAllConditions, removeMultipleConditions }}>
      {children}
    </QueryBuilderContext.Provider>
  );
};

export const useQueryBuilder = (): QueryBuilderContextType => {
  const context = useContext(QueryBuilderContext);
  if (!context) {
    throw new Error('useQueryBuilder must be used within a QueryBuilderProvider');
  }
  return context;
};
